/**
 * Boîte à outils — défi en QCM des outils de physique-chimie : une série de questions (une par
 * « sorte »), quatre choix, correction expliquée, score, « Recommencer ». Le focus passe sur la
 * nouvelle question ; la correction est annoncée (aria-live).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON } from './controls';
import { frenchSpaces } from './format';
import { seededRng } from './random';
import { richNodes } from './rich-text';
import type { Mcq, Rng } from './mcq';

export function McqChallenge<K extends string>({
  kinds,
  make,
  seed,
  testId,
  rich = false,
}: {
  /** une question par sorte, dans l'ordre */
  kinds: readonly K[];
  make: (kind: K, rng: Rng) => Mcq<K>;
  /** graine (tests) ; sinon hasard */
  seed?: number;
  /** préfixe des data-testid : `${testId}-question`, `${testId}-feedback` */
  testId: string;
  /** indices (E_c) et vecteurs (v⃗) mis en forme */
  rich?: boolean;
}) {
  const show = (s: string) => (rich ? richNodes(s) : frenchSpaces(s));
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = kinds.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Mcq<K>>(() => make(kinds[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(make(kinds[c - 1]!, rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p
        ref={titleRef}
        tabIndex={-1}
        className="text-ink-900 text-base outline-none"
        data-testid={`${testId}-question`}
      >
        {show(q.question)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-left text-base font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {show(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid={`${testId}-feedback`}>
              {show(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}
